"use client";

import Image from "next/image";
import Link from "next/link";
import { useParams } from "next/navigation";
import type { KeyboardEvent, MouseEvent } from "react";
import { memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  LuExternalLink,
  LuTriangleAlert as LuAlertTriangle,
} from "react-icons/lu";
import { startViewTransition } from "use-start-view-transition";

import EmbedPlayer, {
  type EmbedURLMetadata,
  useDetectEmbedURLs,
} from "@/components/app/post/embed-player";
import { ContentBlurOverlay } from "@/components/shared/content-blur-overlay";
import { PostAudioPlayer } from "@/components/shared/media-player/post-audio-player";
import { PostImageSection } from "@/components/shared/post-image/post-image-section";
import { TruncatedContent } from "@/components/shared/truncated-content";
import { Button, buttonVariants } from "@/components/ui/button";
import { useFeedContext } from "@/contexts/feed-context";
import { usePostCacheContext } from "@/contexts/post-cache-context";
import { PostContextProvider, usePostContext } from "@/contexts/post-context";
import { UserContext } from "@/contexts/user-context";
import { useTrackViews } from "@/hooks/use-track-views";
import { AutoplayType, DESO_APP_NAME } from "@/utils/constants";
import { feedFreezeDebugLog } from "@/utils/feed-freeze-debug";

import { FeedPostDetails } from "../feed/feed-post-details";
import { PostBody } from "./post-body";
import { PostDetailsHeader, PostDetailsView } from "./post-details-header";
import { PostGatedContent } from "./post-gated-content";
import { PostPoll } from "./post-poll/post-poll";
import { VideoPlayer } from "./post-video-section";

type PostHeaderBodyProps = {
  showRepost?: boolean;
  postType?: PostDetailsView;
  PostBodyClass?: string;
  onClick?: (
    e: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>,
  ) => void;
  onMouseEnter?: (e: MouseEvent<HTMLDivElement>) => void;
};

const INTERSECTION_ACTION_DELAY = 250;

type PostMediaBlock =
  | {
      key: string;
      type: "video";
      url: string;
      index: number;
    }
  | {
      key: string;
      type: "images";
      urls: string[];
    }
  | {
      key: string;
      type: "audio";
      url: string;
    }
  | {
      key: string;
      type: "embed";
      embed: EmbedURLMetadata;
    };

const getMediaCountLabel = (
  count: number,
  singular: string,
  plural = `${singular}s`,
) => {
  return `+${count} ${count === 1 ? singular : plural}`;
};

const getMediaRevealLabel = ({
  hiddenImageCount,
  hiddenVideoCount,
  hiddenAudioCount,
  hiddenEmbedCount,
}: {
  hiddenImageCount: number;
  hiddenVideoCount: number;
  hiddenAudioCount: number;
  hiddenEmbedCount: number;
}) => {
  const hiddenMediaParts = [
    hiddenVideoCount > 0
      ? getMediaCountLabel(hiddenVideoCount, "video")
      : undefined,
    hiddenImageCount > 0
      ? getMediaCountLabel(hiddenImageCount, "image")
      : undefined,
    hiddenAudioCount > 0
      ? getMediaCountLabel(hiddenAudioCount, "audio", "audio")
      : undefined,
    hiddenEmbedCount > 0
      ? getMediaCountLabel(hiddenEmbedCount, "embed")
      : undefined,
  ].filter(Boolean);

  return `Show ${hiddenMediaParts.join(", ")}`;
};

const Blocked = () => (
  <div
    className="
      flex items-center justify-center gap-2 rounded-full border border-border
      bg-card p-2 text-sm
    "
  >
    <Image
      src="/assets/logo-deso-mark.svg"
      alt={`${DESO_APP_NAME} Logo`}
      width={12}
      height={0}
    />
    This post is from a blocked user.
  </div>
);

const PREVIEW_AUDIO_BLOCK_LIMIT = 3;

export const PostHeaderBody = memo(
  ({
    postType = PostDetailsView.details,
    showRepost,
    PostBodyClass,
  }: PostHeaderBodyProps) => {
    const {
      poster,
      innerPost,
      repost,
      isPureRepost,
      post,
      body,
      postHash,
      disableReadMore,
      hideMedia,
      hideEmbeds,
      hideHeader,
      hideAvatar,
      hideHeaderActions,
      hideUpvote,
      postCachePrefix,
      showMoreMaxHeight,
      forwardContextProps,
      mediaMeta,
      isNSFW,
      posterIsNSFW,
    } = usePostContext();

    const { tempFilters } = useFeedContext();
    const { postHash: paramPostHash } = useParams();
    const isMainPost = paramPostHash === postHash;

    const {
      bodyHeight = 0,
      resizeObserver,
      postCacheSignal,
      postCacheKey,
      revealed,
      nsfwRevealed,
      viewIntersecting,
    } = usePostCacheContext(postHash, postCachePrefix);

    const { isUserBlocked } = useContext(UserContext);

    const extraData = isPureRepost ? repost?.extraData : post?.extraData;
    const imageUrls = isPureRepost ? repost?.imageUrls : post?.imageUrls;
    const videoUrls = isPureRepost ? repost?.videoUrls : post?.videoUrls;
    const mediaPostHash = isPureRepost ? repost?.postHash : postHash;
    const postImageUrls = hideMedia ? [] : (imageUrls?.filter(Boolean) ?? []);
    const postVideoUrls = hideMedia ? [] : (videoUrls?.filter(Boolean) ?? []);
    const audioUrls = extraData?.AudioURLs
      ? JSON.parse(extraData?.AudioURLs)
      : undefined;
    const postAudioUrls =
      hideMedia || !Array.isArray(audioUrls) ? [] : audioUrls.filter(Boolean);
    const disabledEmbedUrls = useMemo(() => {
      if (!extraData?.DisabledEmbedURLs) {
        return [];
      }

      const parsed = JSON.parse(extraData.DisabledEmbedURLs);

      return Array.isArray(parsed) ? parsed : [];
    }, [extraData?.DisabledEmbedURLs]);
    const timestamp = isPureRepost ? repost?.timestamp : post?.timestamp;

    const contentContainerRef = useRef<HTMLDivElement>(null);
    const [textRevealState, setTextRevealState] = useState({
      showReveal: false,
      hideTruncate: false,
    });

    const showPostImageSection = postImageUrls.length > 0;
    const showPostVideoSection = postVideoUrls.length > 0;
    const showPostAudioSection = postAudioUrls.length > 0;
    const isMarkdown =
      (isPureRepost ? repost?.extraData?.isMarkdown : extraData.isMarkdown) ===
      "true";
    const extraDataPoll = isPureRepost ? repost?.extraData : extraData;
    const shouldLoadEmbeds = !hideEmbeds && (viewIntersecting || isMainPost);
    const detectedEmbedUrls = useDetectEmbedURLs({
      text: shouldLoadEmbeds
        ? [extraData?.EmbedVideoURL ?? "", body || ""]
        : "",
      cache: true,
    });
    const postEmbedUrls = detectedEmbedUrls.filter(
      ({ url }) => !disabledEmbedUrls.includes(url),
    );
    const showPostEmbedSection = postEmbedUrls.length > 0;
    const hasTruncatableContent = !!body;
    const videoMediaBlocks: PostMediaBlock[] = postVideoUrls.map(
      (url, index) => ({
        key: `video:${url}`,
        type: "video" as const,
        url,
        index,
      }),
    );
    const audioMediaBlocks: PostMediaBlock[] = postAudioUrls.map((url) => ({
      key: `audio:${url}`,
      type: "audio" as const,
      url,
    }));
    const imageMediaBlocks: PostMediaBlock[] =
      postImageUrls.length > 0
        ? [
            {
              key: "images",
              type: "images" as const,
              urls: postImageUrls,
            },
          ]
        : [];
    const embedMediaBlocks: PostMediaBlock[] = postEmbedUrls.map((embed) => ({
      key: `embed:${embed.url}`,
      type: "embed" as const,
      embed,
    }));
    const mediaBlocks = [
      ...videoMediaBlocks,
      ...imageMediaBlocks,
      ...audioMediaBlocks,
      ...embedMediaBlocks,
    ];
    const previewMediaBlocks =
      videoMediaBlocks.length > 0
        ? videoMediaBlocks.slice(0, 1)
        : [
            ...imageMediaBlocks.slice(0, 1),
            ...audioMediaBlocks.slice(0, PREVIEW_AUDIO_BLOCK_LIMIT),
            ...(imageMediaBlocks.length === 0 && audioMediaBlocks.length === 0
              ? embedMediaBlocks.slice(0, 1)
              : []),
          ];
    const previewMediaBlockKeys = new Set(
      previewMediaBlocks.map(({ key }) => key),
    );
    const hiddenPreviewMediaBlocks = mediaBlocks.filter(
      ({ key }) => !previewMediaBlockKeys.has(key),
    );
    const expandedMediaBlocks = [
      ...previewMediaBlocks,
      ...hiddenPreviewMediaBlocks,
    ];
    const isContentRevealed = disableReadMore || !!revealed;
    const visibleMediaBlocks = isContentRevealed
      ? expandedMediaBlocks
      : previewMediaBlocks;
    const hiddenMediaBlocks = isContentRevealed ? [] : hiddenPreviewMediaBlocks;
    const hiddenImageCount = hiddenMediaBlocks.reduce((count, block) => {
      return count + (block.type === "images" ? block.urls.length : 0);
    }, 0);
    const hiddenVideoCount = hiddenMediaBlocks.reduce((count, block) => {
      return count + (block.type === "video" ? 1 : 0);
    }, 0);
    const hiddenAudioCount = hiddenMediaBlocks.reduce((count, block) => {
      return count + (block.type === "audio" ? 1 : 0);
    }, 0);
    const hiddenEmbedCount = hiddenMediaBlocks.reduce((count, block) => {
      return count + (block.type === "embed" ? 1 : 0);
    }, 0);
    const hasVisibleMediaBlocks = visibleMediaBlocks.length > 0;
    const hasHiddenMedia = hiddenMediaBlocks.length > 0;
    const textCanReveal =
      textRevealState.showReveal && !textRevealState.hideTruncate;
    const shouldShowTextReveal =
      !disableReadMore && !isContentRevealed && textCanReveal;
    const shouldShowMediaReveal =
      !disableReadMore && !isContentRevealed && hasHiddenMedia;
    const mediaRevealLabel = getMediaRevealLabel({
      hiddenAudioCount,
      hiddenEmbedCount,
      hiddenImageCount,
      hiddenVideoCount,
    });

    const isPosterBlocked = isUserBlocked(poster?.publicKey);
    const isRepostPosterBlocked = isUserBlocked(repost?.poster?.publicKey);

    useEffect(() => {
      const node = contentContainerRef.current;
      if (!node) return;

      feedFreezeDebugLog("post-header", "body:observe", {
        hasAudio: showPostAudioSection,
        hasBody: !!body,
        hasEmbeds: showPostEmbedSection,
        hasEmbedVideoUrl: !!extraData?.EmbedVideoURL,
        hasImages: showPostImageSection,
        hasVideos: showPostVideoSection,
        postCacheKey,
        postHash,
      });

      resizeObserver.observe(node);

      return () => {
        resizeObserver.unobserve(node);
      };
    }, []);

    const handleRevealClick = () => {
      feedFreezeDebugLog("post-header", "truncate:reveal", {
        bodyHeight,
        postCacheKey,
        postHash,
        wrapperHeight: postCacheSignal.value.wrapperHeight ?? null,
      });
      startViewTransition(() => {
        postCacheSignal.value = {
          ...postCacheSignal.value,
          revealed: true,
        };
      });
    };

    const handleRevealKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }

      event.preventDefault();
      handleRevealClick();
    };

    const handleMediaPlayChange = (playing: boolean) => {
      postCacheSignal.value = {
        ...postCacheSignal.value,
        fixed: playing,
      };
    };

    const renderMediaBlock = (block: PostMediaBlock) => {
      if (block.type === "images") {
        return (
          <PostImageSection
            key={block.key}
            mediaMeta={mediaMeta}
            urls={block.urls}
            postHash={mediaPostHash}
          />
        );
      }

      if (block.type === "audio") {
        return (
          <PostAudioPlayer
            key={block.key}
            src={block.url}
            mediaMeta={mediaMeta}
            postHash={mediaPostHash}
            onPlayChange={handleMediaPlayChange}
          />
        );
      }

      if (block.type === "embed") {
        return (
          <EmbedPlayer
            key={block.key}
            url={block.embed.url}
            detectedUrls={[block.embed]}
          />
        );
      }

      return (
        <VideoPlayer
          key={block.key}
          url={block.url}
          autoplay={
            block.index === 0
              ? isMainPost
                ? AutoplayType.ALWAYS
                : AutoplayType.ON_INTERSECTION
              : AutoplayType.NEVER
          }
          mediaMeta={mediaMeta}
          postHash={mediaPostHash}
          onPlayChange={handleMediaPlayChange}
        />
      );
    };

    const { ref } = useTrackViews({
      postHash: isPureRepost ? (repost?.postHash ?? "") : postHash,
      delay: INTERSECTION_ACTION_DELAY,
      postCachePrefix,
    });

    if (postType === PostDetailsView.feed && isUserBlocked(poster?.publicKey)) {
      return null;
    }

    // You may use these for development
    /*videoUrls = [
    "https://sample-videos.com/video321/mp4/720/big_buck_bunny_720p_1mb.mp4",
    "https://iframe.videodelivery.net/f91f636f600024689d3391d327ab916e",
    "https://lvpr.tv/?v=4c0d71am5dtq9yyo",
  ];*/
    return (
      <div
        className="post-header-body relative mb-2 flex flex-col gap-2 md:gap-4"
        ref={ref}
      >
        {poster &&
          !hideHeader &&
          (isPosterBlocked ? (
            <Blocked />
          ) : (
            <PostDetailsHeader
              {...{
                poster,
                postType,
                timestamp,
              }}
            />
          ))}
        <div className="relative">
          <div className="relative">
            <TruncatedContent
              maxHeight={showMoreMaxHeight}
              disableReadMore={disableReadMore}
              revealed={isContentRevealed}
              height={bodyHeight}
              onReveal={handleRevealClick}
              showRevealButton={false}
              onChange={({ showReveal, hideTruncate }) => {
                setTextRevealState((prev) => {
                  if (
                    prev.showReveal === showReveal &&
                    prev.hideTruncate === hideTruncate
                  ) {
                    return prev;
                  }

                  return { showReveal, hideTruncate };
                });
                feedFreezeDebugLog(
                  "post-header",
                  "truncate:onChange",
                  {
                    bodyHeight,
                    hideTruncate,
                    postCacheKey,
                    postHash,
                    showReveal,
                    wrapperHeight: postCacheSignal.value.wrapperHeight ?? null,
                  },
                  {
                    throttleKey: `truncate-on-change:${postCacheKey}`,
                    throttleMs: 250,
                  },
                );
              }}
            >
              <div
                className="flex max-w-full min-w-0 flex-col gap-4"
                ref={contentContainerRef}
                data-cache-key={postCacheKey}
                data-track="body"
              >
                {body && (
                  <PostBody
                    isMarkdown={isMarkdown}
                    body={body}
                    className={`
                      wrap-break-word
                      ${PostBodyClass}
                    `}
                  />
                )}
              </div>
            </TruncatedContent>
            {shouldShowTextReveal && (
              <div
                aria-label="Show more..."
                className="
                  absolute inset-0 cursor-pointer
                  focus-visible:ring-2 focus-visible:ring-ring
                  focus-visible:outline-none
                "
                onClick={handleRevealClick}
                onKeyDown={handleRevealKeyDown}
                role="button"
                tabIndex={0}
              >
                <div
                  className="
                    pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-linear-to-b
                    from-transparent via-background/80 to-background
                  "
                />
                <span
                  className={buttonVariants({
                    variant: "link",
                    className:
                      "pointer-events-none absolute bottom-0 left-0 pl-0",
                  })}
                >
                  Show more...
                </span>
              </div>
            )}
          </div>
          {hasVisibleMediaBlocks && (
            <div
              className={`
                flex max-w-full min-w-0 flex-col gap-4
                ${hasTruncatableContent ? "mt-4" : ""}
              `}
            >
              {visibleMediaBlocks.map(renderMediaBlock)}
            </div>
          )}
          {shouldShowMediaReveal && (
            <Button onClick={handleRevealClick} variant="link" className="pl-0">
              {mediaRevealLabel}
            </Button>
          )}
          {tempFilters.hideNSFW === "blur" &&
            (isNSFW || posterIsNSFW) &&
            !nsfwRevealed && (
              <ContentBlurOverlay
                buttonText="Show Post"
                onReveal={() => {
                  postCacheSignal.value = {
                    ...postCacheSignal.value,
                    nsfwRevealed: true,
                  };
                }}
              >
                {!posterIsNSFW && (
                  <p className="flex flex-row items-center gap-2">
                    <LuAlertTriangle className="inline-block text-lg" />
                    This post has been tagged as NSFW.
                  </p>
                )}
                {posterIsNSFW && (
                  <p className="flex flex-row items-center gap-2">
                    <LuAlertTriangle className="inline-block text-lg" />
                    This user has been tagged as NSFW.
                  </p>
                )}
              </ContentBlurOverlay>
            )}
        </div>
        {(isPureRepost ? repost?.isNft : post.isNft) && (
          <div className="mt-2 flex w-full">
            <div
              className="
                flex w-full items-center justify-between gap-2 rounded-full
                bg-card p-2
              "
            >
              <div className="flex items-center gap-2 pl-2 text-sm">
                <Image
                  src="/assets/logo-deso-mark.svg"
                  alt={`${DESO_APP_NAME} Logo`}
                  width={12}
                  height={0}
                />
                Minted as NFT
              </div>
              <div>
                <Link
                  href={`https://nftz.me/nft/${isPureRepost ? repost?.postHash : postHash}`}
                  target="_blank"
                  className={buttonVariants({
                    size: "sm",
                    variant: "default",
                    className: "flex gap-1",
                  })}
                >
                  <LuExternalLink className="text-lg" />
                  View NFT
                </Link>
              </div>
            </div>
          </div>
        )}
        {extraDataPoll?.PollWeightType && (
          <PostPoll {...{ extraData: extraDataPoll }} />
        )}
        {poster && <PostGatedContent creator={poster} />}
        {showRepost &&
          innerPost &&
          repost &&
          (isRepostPosterBlocked ? (
            <Blocked />
          ) : (
            <div
              className="
                rounded-2xl border border-border
                hover:bg-accent
              "
            >
              {!innerPost.isHidden && (
                <PostContextProvider
                  post={innerPost}
                  // We have the ability to forward context props to the inner post if needed
                  // Only layout related props, no data related
                  {...(forwardContextProps
                    ? {
                        hideMedia,
                        hideEmbeds,
                        hideHeader,
                        hideAvatar,
                        hideHeaderActions,
                        hideActivity: true,
                        hideUpvote,
                        postCachePrefix,
                        showMoreMaxHeight,
                        forwardContextProps,
                      }
                    : {})}
                  disableReadMore={false}
                >
                  <FeedPostDetails
                    className="xl:p-6 xl:pb-4"
                    showRepost={isPureRepost}
                    clickable
                  />
                </PostContextProvider>
              )}
              {innerPost.isHidden && (
                <div className="p-4 text-center">
                  Quoted post has been removed by the author.
                </div>
              )}
            </div>
          ))}
      </div>
    );
  },
);

PostHeaderBody.displayName = "PostHeaderBody";
